import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Link as MuiLink,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../allauth/auth';
import { AssignmentEditButton, AssignmentRoleActionsProvider } from '../components/AssignmentActions';
import PaymentAssignmentCard from '../components/PaymentAssignmentCard';
import ApiClient from '../config/ApiClient';
import { useAppContext } from '../context/AppContext';
import { PaginatedResponse, PaymentAssignments } from '../object-actions/types/types';
import { assignmentOnboardingPath } from '../utils/onboarding';
import { formatAssignmentPaymentLabel } from '../utils/paymentAssignmentDisplay';
import { resolveTenantPath } from '../utils/tenantUrl';

const PaymentAssignmentsPage: React.FC = () => {
    const auth = useAuth();
    const { organization } = useAppContext();
    const user = auth?.data?.user;
    const [assignments, setAssignments] = useState<PaymentAssignments[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchAssignments = useCallback(async () => {
        if (!user?.id) {
            setError('User not authenticated');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            params.set('payments__author', user.id.toString());
            params.set('manage', 'true');
            params.set('count', 'false');
            if (limit > 0) params.set('limit', limit.toString());
            if (offset > 0) params.set('offset', offset.toString());

            const response = await ApiClient.get<PaginatedResponse<PaymentAssignments>>(
                `/api/payment-assignments/?${params.toString()}`
            );

            if (response.error) {
                setError(response.error);
            } else if (response.success && response.data) {
                const data = response.data;
                if (offset === 0) {
                    setAssignments(data.results || []);
                } else {
                    setAssignments(prev => [...prev, ...(data.results || [])]);
                }
                if (data.count != null && data.count >= 0) {
                    setTotalCount(data.count);
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch program assignments');
        } finally {
            setLoading(false);
        }
    }, [user?.id, limit, offset]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    const handleUpdate = useCallback(() => {
        setOffset(0);
        fetchAssignments();
    }, [fetchAssignments]);

    if (loading && assignments.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Card>
                    <CardContent>
                        <Typography color="error">{error}</Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Program Assignments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage Access to your Programs
                </Typography>
            </Box>

            {assignments.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            You don&apos;t have any program assignments yet. Purchase a program to get
                            started, or contact your administrator for access.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/account/subscriptions"
                            variant="contained"
                            color="secondary"
                            sx={{ mb: 2 }}
                            data-attr="payment-assignments_get-started"
                        >
                            Get started
                        </Button>
                        {organization?.contact_email && (
                            <Typography variant="body2">
                                <a href={`mailto:${organization.contact_email}`}>{organization.contact_email}</a>
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {assignments.map((assignment) => {
                        const paymentLabel = formatAssignmentPaymentLabel(assignment);
                        const continueSetup = resolveTenantPath(
                            assignment.organization?.slug,
                            assignmentOnboardingPath(assignment.id),
                        );
                        return (
                            <Card key={assignment.id} elevation={2}>
                                <AssignmentRoleActionsProvider
                                    assignment={assignment}
                                    onUpdate={handleUpdate}
                                    isPayer
                                >
                                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                flexWrap: 'wrap',
                                                gap: 2,
                                            }}
                                        >
                                            <Typography variant="h6" component="h2">
                                                {paymentLabel}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-end',
                                                    gap: 1,
                                                }}
                                            >
                                                <AssignmentEditButton />
                                                {assignment.id != null && (
                                                    continueSetup.external ? (
                                                        <MuiLink
                                                            href={continueSetup.href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            variant="body2"
                                                            sx={{ textDecoration: 'none' }}
                                                        >
                                                            Continue setup →
                                                        </MuiLink>
                                                    ) : (
                                                        <Typography
                                                            component={RouterLink}
                                                            to={continueSetup.href}
                                                            variant="body2"
                                                            sx={{ textDecoration: 'none' }}
                                                        >
                                                            Continue setup →
                                                        </Typography>
                                                    )
                                                )}
                                            </Box>
                                        </Box>

                                        <PaymentAssignmentCard
                                            assignment={assignment}
                                            showEmptyRoles
                                            showRoleActions
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                borderTop: 1,
                                                borderColor: 'divider',
                                                pt: 2,
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {assignment.organization?.str ?? '-'} |
                                                Assessment {assignment.assessment_id ? `#${assignment.assessment_id}` : '-'}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </AssignmentRoleActionsProvider>
                            </Card>
                        );
                    })}
                </Box>
            )}

            {totalCount > assignments.length && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {assignments.length} of {totalCount} assignments
                    </Typography>
                </Box>
            )}
            {totalCount === 0 && assignments.length >= limit && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {assignments.length} assignments
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default PaymentAssignmentsPage;
